import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, cleanFeed, FEED_TYPES } from '../lib/api';
import type { FeedItem } from '../lib/api';

export type StreamStatus = 'connecting' | 'open' | 'error';

/**
 * Subscribes to the indexer SSE feed (global when `launch` is undefined). EventSource reconnects
 * on its own; `onReconnect` fires when a dropped stream comes back so callers can catch up.
 */
export function useFeedStream(opts: {
  launch?: string;
  enabled?: boolean;
  onItem: (item: FeedItem) => void;
  onReconnect?: () => void;
  /** the indexer rolled back items after a chain reorg; drop any live copies */
  onReorg?: () => void;
}): StreamStatus {
  const { launch, enabled = true } = opts;
  const [status, setStatus] = useState<StreamStatus>('connecting');
  const qc = useQueryClient();
  const handlers = useRef(opts);
  useEffect(() => {
    handlers.current = opts;
  });

  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;
    const es = new EventSource(api.streamUrl(launch));
    let dropped = false;
    const onMessage = (ev: MessageEvent<string>) => {
      try {
        handlers.current.onItem(cleanFeed(JSON.parse(ev.data) as FeedItem));
      } catch {
        /* ignore malformed frames */
      }
    };
    for (const t of FEED_TYPES) es.addEventListener(t, onMessage);
    es.onmessage = onMessage;
    // EventSource resends Last-Event-ID on reconnect; the indexer replays what we missed
    es.addEventListener('reorg', () => {
      handlers.current.onReorg?.();
      void qc.invalidateQueries();
    });
    es.onopen = () => {
      setStatus('open');
      if (dropped) handlers.current.onReconnect?.();
      dropped = false;
    };
    es.onerror = () => {
      dropped = true;
      setStatus('error');
    };
    return () => es.close();
  }, [launch, enabled, qc]);

  return status;
}
